import { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { VList } from "virtua";
import type {
  GlobalArtikel,
  GlobalCatalog,
  Kundenartikel,
  Stueckliste,
  TenantSortiment,
} from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { StuecklisteEditor } from "./StuecklisteEditor";
import { Icon } from "../../shell/Icon";
import { useApi } from "../../hooks/useApi";
import {
  CSV_HEADER_SYNONYMS,
  useCsvImport,
  type CsvImportField,
  type CsvImportMapping,
} from "../../hooks/useCsvImport";
import { useTenant } from "../../app/TenantContext";

/**
 * Catalog & Sortiment — Stufe G1.4b.
 *
 * Zeigt die globale Artikel-Liste (.bidi-handel/catalog/artikel.json) gefiltert
 * + angereichert mit dem Sortiment-Status des aktuellen Tenants:
 *  - „im Sortiment" / „nicht im Sortiment" / „mit Override"
 *  - Effektiver Listenpreis = Override oder Catalog-Listenpreis
 *
 * Aktionen pro Zeile:
 *  - Hinzufuegen / Entfernen aus dem Tenant-Sortiment
 *  - Listenpreis-Override editieren (Modal)
 *  - Soft-Delete (lifecycle=discontinued) — verstecktes Catalog-Item
 *
 * „+ Catalog-Artikel" oben rechts → Create-Modal mit ID-Generator
 * (Pattern: "<warengruppe>-<code>" als deterministische ID).
 */

interface CatalogRow {
  artikel: GlobalArtikel;
  /** Index im Sortiment, -1 wenn nicht enthalten */
  sortimentIdx: number;
  /** Effektiver Listenpreis nach Override-Anwendung */
  effectiveListenpreis: number | undefined;
  /** Effektive Einheit nach Override-Anwendung */
  effectiveEinheit: string | undefined;
}

// Grid-Spalten fuer Header + Rows. Geteilt damit die Spalten alignen.
//   code · bezeichnung(flex) · warengruppe · listenpreis · einheit · status · aktionen
const CATALOG_GRID_COLS =
  "minmax(110px,140px) minmax(220px,1fr) minmax(120px,160px) minmax(100px,120px) minmax(80px,100px) minmax(130px,160px) minmax(200px,220px)";

// Physikalische Einheiten (Spiegel von core/util/einheiten.ts). Ein Artikel mit
// einer NICHT-physikalischen Verkaufseinheit (Gebinde: Sack/Box/Pack…) braucht
// `inhaltProVE`, damit die Bepreisung Positionen in kg/m²/St auf die VE umrechnen
// kann — sonst landet die Position als Klärpunkt. Der Badge treibt die Pflege.
const PHYSISCHE_EINHEITEN = new Set([
  "kg", "g", "t", "to", "m", "lfm", "lfdm", "cm", "mm", "m2", "m²", "qm",
  "m3", "m³", "cbm", "l", "ltr", "st", "st.", "stk", "stk.", "stck", "stueck",
  "stück", "stp", "h", "std", "std.", "stunde", "stunden", "psch", "pausch",
  "pschl", "pau", "pa",
]);
function brauchtInhalt(a: GlobalArtikel): boolean {
  const e = a.einheit?.trim().toLowerCase();
  if (!e) return false; // ohne Einheit: eigener Pfad, hier kein Badge
  if (PHYSISCHE_EINHEITEN.has(e)) return false; // physikalisch → Alias-Pfad reicht
  return a.inhaltProVE === undefined; // Gebinde ohne Inhaltsfaktor → pflegen
}

export function CatalogPage() {
  const { tenantId } = useTenant();
  const catalogRes = useApi(() => api.getCatalog(), [tenantId]);
  const sortimentRes = useApi(() => api.getSortiment(tenantId), [tenantId]);
  const [filter, setFilter] = useState("");
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editArtikel, setEditArtikel] = useState<GlobalArtikel | null>(null);
  const [editOverride, setEditOverride] = useState<{
    artikel: GlobalArtikel;
    sortimentIdx: number;
  } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  const catalog: GlobalCatalog | null =
    catalogRes.state.status === "ok" ? catalogRes.state.data : null;
  const sortimentData =
    sortimentRes.state.status === "ok" ? sortimentRes.state.data : null;
  const sortiment = sortimentData?.sortiment ?? null;

  const rows = useMemo<CatalogRow[]>(() => {
    if (!catalog) return [];
    const sortimentIdxByCatalogId = new Map<string, number>();
    if (sortiment) {
      sortiment.artikel.forEach((r, idx) =>
        sortimentIdxByCatalogId.set(r.catalogArtikelId, idx)
      );
    }
    return catalog.artikel
      .filter((a) => showDiscontinued || a.lifecycle === "active")
      .map((a) => {
        const sIdx = sortimentIdxByCatalogId.get(a.id) ?? -1;
        const ref = sIdx >= 0 ? sortiment!.artikel[sIdx]! : null;
        return {
          artikel: a,
          sortimentIdx: sIdx,
          effectiveListenpreis:
            ref?.listenpreisOverride !== undefined
              ? ref.listenpreisOverride
              : a.listenpreis,
          effectiveEinheit: ref?.einheitOverride ?? a.einheit,
        };
      });
  }, [catalog, sortiment, showDiscontinued]);

  // useDeferredValue: Filter-Input bleibt responsive, die teure Liste rendert
  // mit dem zurueckgestellten Wert — bei grossen Listen (>1k Zeilen) deutlich
  // gefuehlt fluessiger.
  const deferredFilter = useDeferredValue(filter);
  const filtered = useMemo<CatalogRow[]>(() => {
    const q = deferredFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(({ artikel }) =>
      [
        artikel.code,
        artikel.bezeichnung,
        artikel.warengruppe ?? "",
        artikel.kurzText ?? "",
        artikel.suchbegriffe.join(" "),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, deferredFilter]);

  // Stabile Callbacks fuer die Row — damit React.memo greift, wenn der Parent
  // wegen Modal-State-Change re-rendert. Ohne useCallback waeren die Handler
  // bei jedem Render neue Funktionen, Memo waere sinnlos.
  const handleToggleSortiment = useCallback(
    async (row: CatalogRow) => {
      if (!sortiment && !sortimentData) return;
      setError(null);
      const next: TenantSortiment = {
        schemaVersion: 1,
        artikel: sortiment ? [...sortiment.artikel] : [],
      };
      if (row.sortimentIdx >= 0) {
        next.artikel.splice(row.sortimentIdx, 1);
      } else {
        next.artikel.push({
          catalogArtikelId: row.artikel.id,
          active: true,
          addedAt: new Date().toISOString(),
        });
      }
      try {
        await api.putSortiment(tenantId, next);
        sortimentRes.refetch();
      } catch (err) {
        setError(formatError(err));
      }
    },
    [sortiment, sortimentData, sortimentRes, tenantId]
  );

  const handleEditCatalog = useCallback((artikel: GlobalArtikel) => {
    setEditArtikel(artikel);
  }, []);

  const handleEditOverride = useCallback(
    (artikel: GlobalArtikel, sortimentIdx: number) => {
      setEditOverride({ artikel, sortimentIdx });
    },
    []
  );

  const handleSoftDelete = useCallback(
    async (row: CatalogRow) => {
      setError(null);
      try {
        await api.deleteCatalogArtikel(row.artikel.id);
        catalogRes.refetch();
      } catch (err) {
        setError(formatError(err));
      }
    },
    [catalogRes]
  );

  if (catalogRes.state.status === "loading" || sortimentRes.state.status === "loading")
    return <p className="text-sm text-slate-500">Lade Catalog + Sortiment …</p>;
  if (catalogRes.state.status === "error")
    return (
      <ErrorBanner
        title="Catalog nicht ladbar"
        error={catalogRes.state.error.message}
        onRetry={catalogRes.refetch}
      />
    );

  const activeCount = catalog
    ? catalog.artikel.filter((a) => a.lifecycle === "active").length
    : 0;
  const sortimentCount = sortiment?.artikel.length ?? 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs text-slate-500">
            <span className="font-medium text-slate-700">{activeCount}</span>{" "}
            aktive Catalog-Artikel ·{" "}
            <span className="font-medium text-slate-700">{sortimentCount}</span>{" "}
            im Tenant-Sortiment
            {sortimentData?.orphanRefs && sortimentData.orphanRefs.length > 0 && (
              <span className="ml-2 rounded-sm bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
                ⚠ {sortimentData.orphanRefs.length} verwaiste Refs
              </span>
            )}
          </p>
          {!sortiment && (
            <p className="mt-1 text-xs text-amber-700">
              Kein Sortiment vorhanden — bitte auf der Stammdaten-Uebersicht
              die Legacy-Migration starten.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            placeholder="Suche Code / Bezeichnung / Warengruppe …"
            className="input w-80"
          />
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={showDiscontinued}
              onChange={(e) => setShowDiscontinued(e.currentTarget.checked)}
            />
            Auslaufmodelle
          </label>
          <button
            type="button"
            onClick={() => setCsvImportOpen(true)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            title="Lieferanten-CSV in den Catalog upserten und ins Sortiment legen"
          >
            CSV importieren
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            + Catalog-Artikel
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
          <p className="font-medium text-rose-800">Aktion fehlgeschlagen</p>
          <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
        </div>
      )}

      {/* Virtualisierte Liste — bei 50k Artikeln ohne VList unscrollbar.
          Header + Rows teilen denselben grid-template-columns. */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div
          className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
          style={{ display: "grid", gridTemplateColumns: CATALOG_GRID_COLS }}
        >
          <div className="px-3 py-2">Code</div>
          <div className="px-3 py-2">Bezeichnung</div>
          <div className="px-3 py-2">Warengruppe</div>
          <div className="px-3 py-2 text-right">Listenpreis</div>
          <div className="px-3 py-2">Einheit</div>
          <div className="px-3 py-2">Status</div>
          <div className="px-3 py-2"></div>
        </div>
        {filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            {catalog?.artikel.length === 0
              ? "Catalog leer — entweder Legacy-Migration starten oder Artikel manuell anlegen."
              : "Keine Treffer im Filter."}
          </div>
        ) : (
          <VList style={{ height: "min(70vh, 720px)" }}>
            {filtered.map((row) => (
              <CatalogRowView
                key={row.artikel.id}
                row={row}
                onToggleSortiment={handleToggleSortiment}
                onEditCatalog={handleEditCatalog}
                onEditOverride={handleEditOverride}
                onSoftDelete={handleSoftDelete}
              />
            ))}
          </VList>
        )}
      </div>

      {editArtikel && (
        <EditCatalogDialog
          artikel={editArtikel}
          katalogArtikel={catalog?.artikel ?? []}
          onClose={() => setEditArtikel(null)}
          onSaved={() => {
            setEditArtikel(null);
            catalogRes.refetch();
          }}
        />
      )}
      {editOverride && sortiment && (
        <EditOverrideDialog
          tenantId={tenantId}
          sortiment={sortiment}
          artikel={editOverride.artikel}
          sortimentIdx={editOverride.sortimentIdx}
          onClose={() => setEditOverride(null)}
          onSaved={() => {
            setEditOverride(null);
            sortimentRes.refetch();
          }}
        />
      )}
      {createOpen && (
        <CreateCatalogDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            catalogRes.refetch();
          }}
        />
      )}
      {csvImportOpen && (
        <CsvImportDialog
          tenantId={tenantId}
          onClose={() => setCsvImportOpen(false)}
          onImported={() => {
            setCsvImportOpen(false);
            catalogRes.refetch();
            sortimentRes.refetch();
          }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Row
// ───────────────────────────────────────────────────────────────────────

const CatalogRowView = memo(function CatalogRowView({
  row,
  onToggleSortiment,
  onEditCatalog,
  onEditOverride,
  onSoftDelete,
}: {
  row: CatalogRow;
  onToggleSortiment: (row: CatalogRow) => void;
  onEditCatalog: (artikel: GlobalArtikel) => void;
  onEditOverride: (artikel: GlobalArtikel, sortimentIdx: number) => void;
  onSoftDelete: (row: CatalogRow) => void;
}) {
  const a = row.artikel;
  const inSortiment = row.sortimentIdx >= 0;
  const isDiscontinued = a.lifecycle === "discontinued";
  return (
    <div
      className={`text-sm border-b border-slate-100 ${
        isDiscontinued ? "bg-slate-50/60 text-slate-500" : ""
      }`}
      style={{
        display: "grid",
        gridTemplateColumns: CATALOG_GRID_COLS,
        alignItems: "center",
      }}
    >
      <div className="px-3 py-2 min-w-0">
        <button
          type="button"
          onClick={() => onEditCatalog(a)}
          className="font-mono text-xs font-semibold text-slate-900 hover:text-brand-700 hover:underline truncate block"
        >
          {a.code}
        </button>
      </div>
      <div className="px-3 py-2 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm truncate">{a.bezeichnung}</span>
          {a.stueckliste && (
            <span
              className="shrink-0 text-brand-700"
              title={`Set / Stückliste mit ${a.stueckliste.komponenten.length} Komponente(n)`}
              aria-label="Set-Artikel"
            >
              <Icon name="layers" size={13} />
            </span>
          )}
        </div>
        {a.kurzText && a.kurzText !== a.bezeichnung && (
          <div className="text-xs text-slate-500 truncate">{a.kurzText}</div>
        )}
      </div>
      <div className="px-3 py-2 text-xs min-w-0">
        {a.warengruppe ? (
          <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[11px] uppercase tracking-wider text-slate-600">
            {a.warengruppe}
          </span>
        ) : (
          "—"
        )}
      </div>
      <div className="px-3 py-2 text-right font-mono text-xs">
        {row.effectiveListenpreis !== undefined
          ? row.effectiveListenpreis.toFixed(2)
          : "—"}
        {inSortiment &&
          row.effectiveListenpreis !== a.listenpreis &&
          a.listenpreis !== undefined && (
            <span
              className="ml-1 text-[10px] text-amber-600"
              title={`Catalog-Listenpreis: ${a.listenpreis.toFixed(2)}`}
            >
              (Override)
            </span>
          )}
      </div>
      <div className="px-3 py-2 text-xs font-mono">
        {row.effectiveEinheit ?? "—"}
        {brauchtInhalt(a) && (
          <span
            className="ml-1 rounded-sm bg-amber-100 px-1 py-0.5 text-[9px] font-sans font-medium text-amber-700"
            title="Gebinde-Artikel ohne Inhalt pro VE — die Bepreisung kann Positionen in kg/m²/St nicht auf die Verkaufseinheit umrechnen. Bitte Basiseinheit + Inhalt pflegen."
          >
            Inhalt?
          </span>
        )}
      </div>
      <div className="px-3 py-2">
        {isDiscontinued ? (
          <span className="rounded-sm bg-slate-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">
            Auslauf
          </span>
        ) : inSortiment ? (
          <span className="rounded-sm bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
            im Sortiment
          </span>
        ) : (
          <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            nicht im Sortiment
          </span>
        )}
      </div>
      <div className="px-3 py-2">
        <div className="inline-flex gap-1 justify-end w-full">
          {!isDiscontinued && (
            <button
              type="button"
              onClick={() => onToggleSortiment(row)}
              className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${
                inSortiment
                  ? "border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                  : "bg-brand-600 text-white hover:bg-brand-700"
              }`}
              title={inSortiment ? "Aus Sortiment entfernen" : "Ins Sortiment"}
            >
              {inSortiment ? "− Sortiment" : "+ Sortiment"}
            </button>
          )}
          {inSortiment && !isDiscontinued && (
            <button
              type="button"
              onClick={() => onEditOverride(a, row.sortimentIdx)}
              className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
            >
              Override
            </button>
          )}
          {!isDiscontinued && (
            <button
              type="button"
              onClick={() => onSoftDelete(row)}
              className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
              aria-label="Auslaufmodell setzen"
              title="Auslaufmodell setzen"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// ───────────────────────────────────────────────────────────────────────
// Edit Catalog Artikel
// ───────────────────────────────────────────────────────────────────────

function EditCatalogDialog({
  artikel,
  katalogArtikel,
  onClose,
  onSaved,
}: {
  artikel: GlobalArtikel;
  katalogArtikel: GlobalArtikel[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [bezeichnung, setBezeichnung] = useState(artikel.bezeichnung);
  const [kurzText, setKurzText] = useState(artikel.kurzText ?? "");
  const [warengruppe, setWarengruppe] = useState(artikel.warengruppe ?? "");
  const [listenpreis, setListenpreis] = useState(
    artikel.listenpreis !== undefined ? String(artikel.listenpreis) : ""
  );
  const [einheit, setEinheit] = useState(artikel.einheit ?? "");
  const [basiseinheit, setBasiseinheit] = useState(artikel.basiseinheit ?? "");
  const [inhaltProVE, setInhaltProVE] = useState(
    artikel.inhaltProVE !== undefined ? String(artikel.inhaltProVE) : ""
  );
  const [suchbegriffe, setSuchbegriffe] = useState(
    artikel.suchbegriffe.join(", ")
  );
  // null = (noch) keine Stückliste / wurde entfernt. `null` im PATCH löscht eine
  // bestehende Stückliste; ein Objekt setzt/überschreibt sie.
  const [stueckliste, setStueckliste] = useState<Stueckliste | null>(
    artikel.stueckliste ?? null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const patch: Partial<GlobalArtikel> = {
        bezeichnung,
        kurzText: kurzText || undefined,
        warengruppe: warengruppe || undefined,
        listenpreis: listenpreis === "" ? undefined : Number(listenpreis),
        einheit: einheit || undefined,
        basiseinheit: basiseinheit || undefined,
        inhaltProVE: inhaltProVE === "" ? undefined : Number(inhaltProVE),
        suchbegriffe: suchbegriffe
          .split(/[,;]/)
          .map((s) => s.trim())
          .filter(Boolean),
        // `null` entfernt eine bestehende Stückliste (Schema: nullish).
        stueckliste: stueckliste,
      };
      await api.patchCatalogArtikel(artikel.id, patch);
      onSaved();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell title="Catalog-Artikel bearbeiten" onClose={onClose}>
      <DialogFieldList>
        <DialogField label="ID (read-only)">
          <code className="font-mono text-xs text-slate-500">{artikel.id}</code>
        </DialogField>
        <DialogField label="Code (read-only)">
          <code className="font-mono text-xs text-slate-700">
            {artikel.code}
          </code>
        </DialogField>
        <DialogField label="Bezeichnung">
          <input
            type="text"
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.currentTarget.value)}
            className="input w-full"
          />
        </DialogField>
        <DialogField label="KurzText">
          <input
            type="text"
            value={kurzText}
            onChange={(e) => setKurzText(e.currentTarget.value)}
            className="input w-full"
          />
        </DialogField>
        <DialogField label="Warengruppe">
          <input
            type="text"
            value={warengruppe}
            onChange={(e) => setWarengruppe(e.currentTarget.value)}
            className="input w-full"
          />
        </DialogField>
        <DialogField label="Listenpreis (EUR)">
          <input
            type="number"
            min={0}
            step={0.01}
            value={listenpreis}
            onChange={(e) => setListenpreis(e.currentTarget.value)}
            className="input w-full"
          />
        </DialogField>
        <DialogField label="Einheit (Verkaufs-/Preiseinheit)">
          <input
            type="text"
            value={einheit}
            onChange={(e) => setEinheit(e.currentTarget.value)}
            className="input w-full"
            placeholder="m2 / m3 / St / Sack / Box …"
          />
        </DialogField>
        <DialogField
          label="Basiseinheit"
          hint="physikalische Einheit der LV-Positionen (kg/St/m²) — nur bei Gebinde nötig"
        >
          <input
            type="text"
            value={basiseinheit}
            onChange={(e) => setBasiseinheit(e.currentTarget.value)}
            className="input w-full"
            placeholder="z.B. kg / St / m2"
          />
        </DialogField>
        <DialogField
          label="Inhalt pro VE"
          hint="Basiseinheiten pro Verkaufseinheit, z.B. 25 = 25 kg/Sack. Leer = Preis gilt pro Einheit."
        >
          <input
            type="number"
            min={0}
            step="any"
            value={inhaltProVE}
            onChange={(e) => setInhaltProVE(e.currentTarget.value)}
            className="input w-full"
            placeholder="(leer = Preis pro Einheit)"
          />
        </DialogField>
        <DialogField
          label="Suchbegriffe"
          hint="komma- oder semikolon-separiert"
        >
          <input
            type="text"
            value={suchbegriffe}
            onChange={(e) => setSuchbegriffe(e.currentTarget.value)}
            className="input w-full"
          />
        </DialogField>
      </DialogFieldList>

      <StuecklisteEditor
        value={stueckliste}
        onChange={setStueckliste}
        katalogArtikel={katalogArtikel}
      />

      {error && (
        <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 font-mono text-xs text-rose-700">
          {error}
        </p>
      )}

      <DialogFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? "Speichere …" : "Speichern"}
        </button>
      </DialogFooter>
    </DialogShell>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Edit Tenant-Override
// ───────────────────────────────────────────────────────────────────────

function EditOverrideDialog({
  tenantId,
  sortiment,
  artikel,
  sortimentIdx,
  onClose,
  onSaved,
}: {
  tenantId: string;
  sortiment: TenantSortiment;
  artikel: GlobalArtikel;
  sortimentIdx: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ref = sortiment.artikel[sortimentIdx]!;
  const [listenpreisOverride, setListenpreisOverride] = useState(
    ref.listenpreisOverride !== undefined ? String(ref.listenpreisOverride) : ""
  );
  const [einheitOverride, setEinheitOverride] = useState(
    ref.einheitOverride ?? ""
  );
  const [active, setActive] = useState(ref.active);
  const [bemerkung, setBemerkung] = useState(ref.bemerkung ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const next: TenantSortiment = {
        schemaVersion: 1,
        artikel: sortiment.artikel.map((r, idx) =>
          idx === sortimentIdx
            ? {
                ...r,
                listenpreisOverride:
                  listenpreisOverride === ""
                    ? undefined
                    : Number(listenpreisOverride),
                einheitOverride: einheitOverride || undefined,
                active,
                bemerkung: bemerkung || undefined,
              }
            : r
        ),
      };
      await api.putSortiment(tenantId, next);
      onSaved();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell title="Tenant-Override" onClose={onClose}>
      <p className="mb-3 rounded-md bg-slate-50 p-3 text-sm">
        <span className="font-mono text-xs font-semibold text-slate-700">
          {artikel.code}
        </span>
        <span className="ml-2 text-slate-700">{artikel.bezeichnung}</span>
        <br />
        <span className="text-xs text-slate-500">
          Catalog-Listenpreis:{" "}
          {artikel.listenpreis !== undefined
            ? `${artikel.listenpreis.toFixed(2)} EUR`
            : "—"}{" "}
          · Catalog-Einheit: {artikel.einheit ?? "—"}
        </span>
      </p>

      <DialogFieldList>
        <DialogField
          label="Listenpreis-Override (EUR)"
          hint="leer = Catalog-Preis nutzen"
        >
          <input
            type="number"
            min={0}
            step={0.01}
            value={listenpreisOverride}
            onChange={(e) => setListenpreisOverride(e.currentTarget.value)}
            className="input w-full"
            placeholder="(Catalog-Preis)"
          />
        </DialogField>
        <DialogField
          label="Einheit-Override"
          hint="leer = Catalog-Einheit nutzen"
        >
          <input
            type="text"
            value={einheitOverride}
            onChange={(e) => setEinheitOverride(e.currentTarget.value)}
            className="input w-full"
            placeholder={artikel.einheit ?? "z.B. m2"}
          />
        </DialogField>
        <DialogField label="Aktiv im Sortiment">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.currentTarget.checked)}
            />
            {active ? "aktiv — fuer Match/Bepreisung sichtbar" : "inaktiv"}
          </label>
        </DialogField>
        <DialogField label="Bemerkung">
          <input
            type="text"
            value={bemerkung}
            onChange={(e) => setBemerkung(e.currentTarget.value)}
            className="input w-full"
            placeholder="Notiz nur fuer diesen Tenant"
          />
        </DialogField>
      </DialogFieldList>

      {error && (
        <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 font-mono text-xs text-rose-700">
          {error}
        </p>
      )}

      <DialogFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? "Speichere …" : "Override speichern"}
        </button>
      </DialogFooter>
    </DialogShell>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Create Catalog Artikel
// ───────────────────────────────────────────────────────────────────────

function CreateCatalogDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [code, setCode] = useState("");
  const [bezeichnung, setBezeichnung] = useState("");
  const [warengruppe, setWarengruppe] = useState("");
  const [einheit, setEinheit] = useState("");
  const [basiseinheit, setBasiseinheit] = useState("");
  const [inhaltProVE, setInhaltProVE] = useState("");
  const [listenpreis, setListenpreis] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const id = useMemo(
    () => (code.trim() ? `manual-${code.trim().toUpperCase()}` : ""),
    [code]
  );

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const neu: Kundenartikel & Partial<GlobalArtikel> & { id: string } = {
        id,
        code: code.trim(),
        bezeichnung: bezeichnung.trim(),
        warengruppe: warengruppe || undefined,
        einheit: einheit || undefined,
        basiseinheit: basiseinheit || undefined,
        inhaltProVE: inhaltProVE === "" ? undefined : Number(inhaltProVE),
        listenpreis: listenpreis === "" ? undefined : Number(listenpreis),
        suchbegriffe: [],
      };
      // GlobalArtikel hat extra Pflicht-Felder mit Defaults; das Schema
      // füllt sie beim Parse.
      const full = {
        ...neu,
        fachdaten: {},
        externalRefs: {},
        lifecycle: "active" as const,
        createdAt: new Date().toISOString(),
      };
      await api.createCatalogArtikel(full as GlobalArtikel);
      onCreated();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  const canSave =
    code.trim().length > 0 && bezeichnung.trim().length > 0 && !saving;

  return (
    <DialogShell title="Catalog-Artikel anlegen" onClose={onClose}>
      <DialogFieldList>
        <DialogField label="Code" hint="muss global eindeutig sein">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            className="input w-full font-mono"
            placeholder="z.B. ROH-KS-240"
            autoFocus
          />
        </DialogField>
        <DialogField label="ID (auto)">
          <code className="font-mono text-xs text-slate-500">
            {id || "manual-<code>"}
          </code>
        </DialogField>
        <DialogField label="Bezeichnung">
          <input
            type="text"
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.currentTarget.value)}
            className="input w-full"
          />
        </DialogField>
        <DialogField label="Warengruppe">
          <input
            type="text"
            value={warengruppe}
            onChange={(e) => setWarengruppe(e.currentTarget.value)}
            className="input w-full"
          />
        </DialogField>
        <DialogField label="Einheit (Verkaufs-/Preiseinheit)">
          <input
            type="text"
            value={einheit}
            onChange={(e) => setEinheit(e.currentTarget.value)}
            className="input w-full"
            placeholder="m2 / m3 / St / Sack / Box"
          />
        </DialogField>
        <DialogField
          label="Basiseinheit"
          hint="nur bei Gebinde nötig (kg/St/m²)"
        >
          <input
            type="text"
            value={basiseinheit}
            onChange={(e) => setBasiseinheit(e.currentTarget.value)}
            className="input w-full"
            placeholder="z.B. kg / St / m2"
          />
        </DialogField>
        <DialogField label="Inhalt pro VE" hint="z.B. 25 = 25 kg/Sack">
          <input
            type="number"
            min={0}
            step="any"
            value={inhaltProVE}
            onChange={(e) => setInhaltProVE(e.currentTarget.value)}
            className="input w-full"
            placeholder="(leer = Preis pro Einheit)"
          />
        </DialogField>
        <DialogField label="Listenpreis (EUR)">
          <input
            type="number"
            min={0}
            step={0.01}
            value={listenpreis}
            onChange={(e) => setListenpreis(e.currentTarget.value)}
            className="input w-full"
          />
        </DialogField>
      </DialogFieldList>

      {error && (
        <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 font-mono text-xs text-rose-700">
          {error}
        </p>
      )}

      <DialogFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? "Lege an …" : "Anlegen"}
        </button>
      </DialogFooter>
    </DialogShell>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Shared Dialog-UI
// ───────────────────────────────────────────────────────────────────────

function DialogShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <h3 className="text-base font-medium tracking-tight text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Schliessen"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DialogFieldList({ children }: { children: React.ReactNode }) {
  // 2-spaltig auf breiteren Dialogen — nutzt die Modal-Breite, statt Felder
  // über die volle Breite zu strecken.
  return (
    <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">{children}</div>
  );
}

function DialogField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-0.5 block text-[11px] text-slate-400">{hint}</span>
      )}
    </label>
  );
}

function DialogFooter({ children }: { children: React.ReactNode }) {
  return <div className="mt-5 flex justify-end gap-2">{children}</div>;
}

function ErrorBanner({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
      <p className="text-sm font-medium text-rose-800">{title}</p>
      <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded-md border border-rose-300 bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
      >
        Nochmal versuchen
      </button>
    </div>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { hint?: string; error?: string } | null;
    return body?.hint ?? body?.error ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

// ───────────────────────────────────────────────────────────────────────
// CSV-Import-Dialog: Lieferanten-CSV → Catalog upsert + Sortiment add
// ───────────────────────────────────────────────────────────────────────

type ImportRowInput = {
  code: string;
  bezeichnung: string;
  kurzText?: string;
  ean?: string;
  vpe?: string;
  einheit?: string;
  basiseinheit?: string;
  inhaltProVE?: number;
  warengruppe?: string;
  listenpreis?: number;
  suchbegriffe?: string[];
};

function buildImportRow(
  row: Record<string, string>,
  mapping: CsvImportMapping
): ImportRowInput {
  const get = (field: CsvImportField): string | undefined => {
    const col = mapping[field];
    if (!col) return undefined;
    const v = row[col]?.trim();
    return v && v !== "" ? v : undefined;
  };
  const numeric = (field: CsvImportField): number | undefined => {
    const raw = get(field);
    if (!raw) return undefined;
    const n = Number(raw.replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  };
  const suchbegriffeRaw = get("suchbegriffe");
  return {
    code: get("code") ?? "",
    bezeichnung: get("bezeichnung") ?? "",
    kurzText: get("kurzText"),
    ean: get("ean"),
    vpe: get("vpe"),
    einheit: get("einheit"),
    basiseinheit: get("basiseinheit"),
    inhaltProVE: numeric("inhaltProVE"),
    warengruppe: get("warengruppe"),
    listenpreis: numeric("listenpreis"),
    suchbegriffe: suchbegriffeRaw
      ? suchbegriffeRaw
          .split(/[,;|]/)
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined,
  };
}

function CsvImportDialog({
  tenantId,
  onClose,
  onImported,
}: {
  tenantId: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const [onConflict, setOnConflict] = useState<"update" | "skip">("update");
  const [addToSortiment, setAddToSortiment] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [report, setReport] = useState<{
    created: number;
    updated: number;
    skipped: Array<{ code: string; reason: string }>;
    sortimentAdded: number;
    totalProcessed: number;
  } | null>(null);
  const {
    csvRows,
    headers,
    mapping,
    setMapping,
    parseError,
    dragging,
    setDragging,
    handleFileInput,
    handleDrop,
    reset: resetParser,
  } = useCsvImport({ onBeforeParse: () => setImportError(null) });

  const previewRows: ImportRowInput[] | null = useMemo(() => {
    if (!csvRows || !mapping) return null;
    return csvRows.map((row) => buildImportRow(row, mapping));
  }, [csvRows, mapping]);

  const validation = useMemo(() => {
    if (!previewRows) return null;
    const empty = previewRows.filter(
      (r) => !r.code.trim() || !r.bezeichnung.trim()
    );
    if (empty.length > 0) {
      return {
        ok: false as const,
        count: previewRows.length,
        invalid: empty.length,
      };
    }
    // Duplikate innerhalb der CSV (case-sensitive auf code)
    const codeCount = new Map<string, number>();
    for (const r of previewRows) {
      codeCount.set(r.code, (codeCount.get(r.code) ?? 0) + 1);
    }
    const dups = [...codeCount.entries()].filter(([, c]) => c > 1);
    return {
      ok: true as const,
      count: previewRows.length,
      duplicatesInCsv: dups.length,
    };
  }, [previewRows]);

  async function handleImport() {
    if (!previewRows || !validation?.ok) return;
    setImporting(true);
    setImportError(null);
    setReport(null);
    try {
      const result = await api.importCatalog({
        artikel: previewRows,
        onConflict,
        addToSortimentForTenant: addToSortiment ? tenantId : undefined,
      });
      setReport(result);
    } catch (err) {
      setImportError(formatError(err));
    } finally {
      setImporting(false);
    }
  }

  function resetAll() {
    resetParser();
    setImportError(null);
    setReport(null);
  }

  return (
    <DialogShell title="CSV-Import → Catalog" onClose={onClose}>
      <div className="space-y-4">
        {!csvRows && !report && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`rounded-lg border-2 border-dashed p-10 text-center transition ${
              dragging
                ? "border-brand-500 bg-brand-50"
                : "border-slate-300 bg-white"
            }`}
          >
            <p className="text-sm font-medium text-slate-700">
              CSV-Datei hier ablegen
            </p>
            <p className="mt-1 text-xs text-slate-500">
              oder
              <label className="ml-1 cursor-pointer text-brand-700 underline">
                Datei waehlen
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleFileInput}
                />
              </label>
            </p>
            <p className="mt-3 text-xs text-slate-400">
              Komma/Semikolon-Trenner auto · Header-Zeile noetig · Spalten{" "}
              <code>code</code>, <code>bezeichnung</code>,{" "}
              <code>listenpreis</code>, <code>einheit</code>,{" "}
              <code>warengruppe</code> werden auto-erkannt.
            </p>
            {parseError && (
              <p className="mt-3 text-xs text-rose-600">{parseError}</p>
            )}
          </div>
        )}

        {csvRows && !report && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                {csvRows.length} Zeilen geparst
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                ← Andere Datei waehlen
              </button>
            </div>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Spalten-Zuordnung
              </h3>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {(
                  Object.keys(CSV_HEADER_SYNONYMS) as CsvImportField[]
                ).map((field) => (
                  <label
                    key={field}
                    className="flex items-center gap-2 text-sm"
                  >
                    <span className="w-28 font-mono text-xs text-slate-500">
                      {field}
                    </span>
                    <select
                      value={mapping?.[field] ?? ""}
                      onChange={(e) =>
                        setMapping((m) =>
                          m
                            ? { ...m, [field]: e.currentTarget.value || null }
                            : m
                        )
                      }
                      className="input flex-1 text-xs"
                    >
                      <option value="">— ignorieren —</option>
                      {headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Preview (erste 5 Zeilen)
              </h3>
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      <th className="px-2 py-1">code</th>
                      <th className="px-2 py-1">bezeichnung</th>
                      <th className="px-2 py-1">warengruppe</th>
                      <th className="px-2 py-1 text-right">listenpreis</th>
                      <th className="px-2 py-1">einheit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {previewRows?.slice(0, 5).map((r, idx) => (
                      <tr key={idx}>
                        <td className="px-2 py-1 font-mono">
                          {r.code || <em className="text-rose-500">fehlt</em>}
                        </td>
                        <td className="px-2 py-1">
                          {r.bezeichnung || (
                            <em className="text-rose-500">fehlt</em>
                          )}
                        </td>
                        <td className="px-2 py-1">{r.warengruppe ?? "—"}</td>
                        <td className="px-2 py-1 text-right">
                          {r.listenpreis ?? "—"}
                        </td>
                        <td className="px-2 py-1">{r.einheit ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Import-Optionen
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-4 text-xs">
                  <span className="font-medium text-slate-700">
                    Bei Code-Konflikt:
                  </span>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="onConflict"
                      checked={onConflict === "update"}
                      onChange={() => setOnConflict("update")}
                    />
                    Update (CSV ueberschreibt)
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="onConflict"
                      checked={onConflict === "skip"}
                      onChange={() => setOnConflict("skip")}
                    />
                    Skip (bestehender Eintrag bleibt)
                  </label>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={addToSortiment}
                    onChange={(e) =>
                      setAddToSortiment(e.currentTarget.checked)
                    }
                  />
                  Importierte Artikel ins Tenant-Sortiment{" "}
                  <span className="mono">{tenantId}</span> aufnehmen
                </label>
              </div>
            </section>

            {validation && !validation.ok && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
                <p className="font-medium text-rose-800">
                  {validation.invalid} Zeilen ohne code oder bezeichnung —
                  Mapping korrigieren oder CSV bereinigen.
                </p>
              </div>
            )}

            {validation && validation.ok && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
                <p className="font-medium text-emerald-800">
                  ✓ {validation.count} Zeilen valide. Catalog-Upsert per
                  Code + {addToSortiment ? "Sortiment-Add aktiviert" : "kein Sortiment-Add"}.
                  {validation.duplicatesInCsv > 0 && (
                    <span className="ml-2 font-normal text-amber-700">
                      ⚠ {validation.duplicatesInCsv} Duplikate in der CSV — letzter Eintrag gewinnt.
                    </span>
                  )}
                </p>
              </div>
            )}

            {importError && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
                <p className="font-medium text-rose-800">
                  Import fehlgeschlagen
                </p>
                <p className="mt-1 font-mono text-xs text-rose-700">
                  {importError}
                </p>
              </div>
            )}
          </>
        )}

        {report && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm">
            <p className="text-base font-semibold text-emerald-900">
              ✓ Import abgeschlossen
            </p>
            <ul className="mt-2 space-y-0.5 text-emerald-800">
              <li>
                <span className="font-mono">{report.created}</span> neu angelegt
              </li>
              <li>
                <span className="font-mono">{report.updated}</span> aktualisiert
              </li>
              <li>
                <span className="font-mono">{report.skipped.length}</span>{" "}
                uebersprungen
              </li>
              <li>
                <span className="font-mono">{report.sortimentAdded}</span> ins
                Sortiment aufgenommen
              </li>
              <li className="text-xs text-emerald-700">
                ({report.totalProcessed} CSV-Zeilen verarbeitet)
              </li>
            </ul>
            {report.skipped.length > 0 && (
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-emerald-700">
                  Uebersprungene Eintraege
                </summary>
                <ul className="mt-1 space-y-0.5 font-mono">
                  {report.skipped.slice(0, 20).map((s, idx) => (
                    <li key={idx}>
                      {s.code} — {s.reason}
                    </li>
                  ))}
                  {report.skipped.length > 20 && (
                    <li>… und {report.skipped.length - 20} weitere</li>
                  )}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <DialogFooter>
        {report ? (
          <button
            type="button"
            onClick={onImported}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700"
          >
            Fertig
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={!validation?.ok || importing}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
            >
              {importing
                ? "Importiere …"
                : csvRows
                  ? `${csvRows.length} Zeilen importieren`
                  : "Importieren"}
            </button>
          </>
        )}
      </DialogFooter>
    </DialogShell>
  );
}
